import React, { useEffect, useMemo, useState, useRef } from 'react';
import { toast } from 'sonner';
import {
  CognitoIdentityProviderClient,
  ChangePasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/api';
import { userPoolId } from '../config/cognito-config';
import { Field } from '../components/Field';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { humanizeError } from '../lib/errors';
import { routeCopy } from '../routes';

/** h1 and description, from the route registry (DESIGN_SYSTEM.md §6 Naming). */
const PAGE = routeCopy('/account-settings');

const cognitoClient = new CognitoIdentityProviderClient({
  region: userPoolId.split('_')[0],
});

/**
 * humanizeError matches on message text, but Cognito SDK v3 errors carry their
 * exception code in `name` (e.g. "CodeMismatchException") while `message` is
 * prose. Fold the name into the text so the existing Cognito patterns in
 * lib/errors.ts can match — no mapping logic lives in this page.
 */
const errorText = (err: unknown): string =>
  err instanceof Error ? `${err.name}: ${err.message}` : typeof err === 'string' ? err : ''; // design-system-allow: feeds humanizeError, never the UI — every call site is humanizeError(errorText(err), …)

// ---------------------------------------------------------------------------
// Profile section
// ---------------------------------------------------------------------------

interface ProfileFormState {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

const ProfileSection: React.FC<{ accessToken: string; onSaved: () => void }> = ({ accessToken, onSaved }) => {
  const { user } = useUser();
  const [form, setForm] = useState<ProfileFormState>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });
  const [saving, setSaving] = useState(false);

  /** What the form was last seeded with from the saved profile. */
  const seededRef = useRef<ProfileFormState | null>(null);
  useEffect(() => {
    if (!user) return;
    const saved: ProfileFormState = {
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      phoneNumber: user.phoneNumber ?? '',
    };
    // Re-seed only a form nobody has touched. `user` is refreshed in the
    // background (after a save, and by the shell), and copying it over typed
    // input would silently discard it — Save would even go back to disabled.
    setForm((current) => {
      const seeded = seededRef.current;
      const untouched =
        !seeded ||
        (current.firstName === seeded.firstName &&
          current.lastName === seeded.lastName &&
          current.phoneNumber === seeded.phoneNumber);
      seededRef.current = saved;
      return untouched ? saved : current;
    });
  }, [user]);

  const handleChange = (field: keyof ProfileFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  /** Only what differs from the saved profile; Save is disabled while it is empty. */
  const payload = useMemo(() => {
    const next: Record<string, string> = {};
    if (!user) return next;
    if (form.firstName !== (user.firstName ?? '')) next.firstName = form.firstName;
    if (form.lastName !== (user.lastName ?? '')) next.lastName = form.lastName;
    if (form.phoneNumber !== (user.phoneNumber ?? '')) next.phoneNumber = form.phoneNumber;
    return next;
  }, [form, user]);
  const isDirty = Object.keys(payload).length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !isDirty) return;

    setSaving(true);
    try {
      await apiService.updateUserProfile(accessToken, payload);
      toast.success('Profile saved');
      onSaved();
    } catch (err: unknown) {
      console.error('Failed to save profile:', err);
      toast.error(humanizeError(errorText(err), "Couldn't save your profile"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name">
              {(control) => (
                <Input
                  {...control}
                  value={form.firstName}
                  onChange={handleChange('firstName')}
                  placeholder="First name"
                  autoComplete="given-name"
                />
              )}
            </Field>
            <Field label="Last name">
              {(control) => (
                <Input
                  {...control}
                  value={form.lastName}
                  onChange={handleChange('lastName')}
                  placeholder="Last name"
                  autoComplete="family-name"
                />
              )}
            </Field>
          </div>
          <Field label="Phone number" help="Include country code (e.g. +1 for US). Used for MFA.">
            {(control) => (
              <Input
                {...control}
                type="tel"
                value={form.phoneNumber}
                onChange={handleChange('phoneNumber')}
                placeholder="+1 555 000 0000"
              />
            )}
          </Field>

          <Button type="submit" loading={saving} disabled={!isDirty}>
            Save profile
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Email address section (read-only)
// ---------------------------------------------------------------------------

/**
 * The address is shown, never edited.
 *
 * This card used to run the whole change flow from the browser: a taken-address
 * pre-check against the API, `UpdateUserAttributes` to Cognito to send a code,
 * then `VerifyUserAttribute` followed by a separate `PATCH /portal/me`. Two
 * things were wrong with it. The sign-in address and the platform's own record
 * were written by two independent calls with nothing to reconcile a failure
 * between them, so a dropped second call left the two permanently disagreeing.
 * And an address written to the database this way is what member invitations
 * are matched against, so claiming someone else's address here meant inheriting
 * the organization membership later invited to it. The API now refuses the
 * field outright; the flow is gone rather than guarded.
 */
const EmailSection: React.FC = () => {
  const { user } = useUser();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email address</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          <Label>Current email</Label>
          <p className="text-sm">{user?.email}</p>
          {/* Same 12px muted line a Field uses for help text — this is help
              text, and the address above it is the value. */}
          <p className="text-xs text-muted-foreground">
            Your email address comes from your sign-in provider and can&apos;t be changed here.
            Contact your system administrator if it needs to change.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Change password section
// ---------------------------------------------------------------------------

interface PasswordFormState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordFieldErrors {
  current?: string;
  next?: string;
  confirm?: string;
}

const ChangePasswordSection: React.FC<{ accessToken: string }> = ({ accessToken }) => {
  const [form, setForm] = useState<PasswordFormState>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<PasswordFieldErrors>({});

  /** Which errors a field's change makes stale: its own, and a match that depends on it. */
  const CLEARS: Record<keyof PasswordFormState, Array<keyof PasswordFieldErrors>> = {
    currentPassword: ['current'],
    newPassword: ['next', 'confirm'],
    confirmPassword: ['confirm'],
  };

  const handleChange = (field: keyof PasswordFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    // A field's error clears as soon as that field changes; the others stay.
    setErrors((prev) => {
      const next = { ...prev };
      CLEARS[field].forEach((key) => delete next[key]);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Every rule at once, each under its own field (DESIGN_SYSTEM §5 Validation).
    const found: PasswordFieldErrors = {};
    if (!form.currentPassword) found.current = 'Current password is required.';
    if (form.newPassword.length < 8) found.next = 'New password must be at least 8 characters.';
    if (form.newPassword !== form.confirmPassword) found.confirm = 'New passwords do not match.';
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    setSaving(true);
    setErrors({});
    try {
      await cognitoClient.send(new ChangePasswordCommand({
        AccessToken: accessToken,
        PreviousPassword: form.currentPassword,
        ProposedPassword: form.newPassword,
      }));
      toast.success('Password changed');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: unknown) {
      console.error('Failed to change password:', err);
      const name = err instanceof Error ? err.name : '';
      if (name === 'NotAuthorizedException') {
        setErrors({ current: humanizeError(errorText(err)) });
      } else if (name === 'InvalidPasswordException') {
        setErrors({ next: humanizeError(errorText(err)) });
      } else {
        toast.error(humanizeError(errorText(err), "Couldn't change your password"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change password</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Current password" error={errors.current} required>
            {(control) => (
              <Input
                {...control}
                type="password"
                value={form.currentPassword}
                onChange={handleChange('currentPassword')}
                autoComplete="current-password"
              />
            )}
          </Field>
          <Separator />
          <Field label="New password" error={errors.next} required>
            {(control) => (
              <Input
                {...control}
                type="password"
                value={form.newPassword}
                onChange={handleChange('newPassword')}
                autoComplete="new-password"
              />
            )}
          </Field>
          <Field label="Confirm new password" error={errors.confirm} required>
            {(control) => (
              <Input
                {...control}
                type="password"
                value={form.confirmPassword}
                onChange={handleChange('confirmPassword')}
                autoComplete="new-password"
              />
            )}
          </Field>

          <Button type="submit" variant="outline" loading={saving}>
            Change password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/** One entry per card, in render order: how many field rows, and whether it ends in a button. */
const SECTION_SKELETONS: Array<{ fields: number; action: boolean }> = [
  { fields: 3, action: true }, // Profile
  { fields: 1, action: false }, // Email address — read-only, no submit
  { fields: 3, action: true }, // Change password
];

const AccountSettingsSkeleton: React.FC = () => (
  <div className="max-w-lg space-y-6">
    {SECTION_SKELETONS.map(({ fields, action }, i) => (
      <Card key={i}>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: fields }).map((_, j) => (
            <div key={j} className="space-y-1.5">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
          {action && <Skeleton className="h-9 w-36" />}
        </CardContent>
      </Card>
    ))}
  </div>
);

const AccountSettings: React.FC = () => {
  const { accessToken, user, isLoading, refreshUser } = useUser();

  if (!accessToken || (!user && isLoading)) {
    return (
      <div className="space-y-6">
        <PageHeader title={PAGE.title} description={PAGE.description} />
        <AccountSettingsSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={PAGE.title} description={PAGE.description} />
      <div className="max-w-lg space-y-6">
        <ProfileSection accessToken={accessToken} onSaved={refreshUser} />
        <EmailSection />
        <ChangePasswordSection accessToken={accessToken} />
      </div>
    </div>
  );
};

export default AccountSettings;
